import { useState } from "react";
import * as XLSX from "xlsx";
import { putApi } from "../../services/api/globalApi";
import {
  errorMessage,
  successMessage,
} from "../../utils/toastify-message/ToastifyMessage";
import { digitsThree } from "../../utils/price";

function normalizeKey(key) {
  return key.replace(/^\uFEFF/, "").trim();
}

function normalizeRow(row) {
  const normalized = {};
  for (const key of Object.keys(row)) {
    normalized[normalizeKey(key)] = row[key];
  }
  return normalized;
}

function parseWorkbook(arrayBuffer) {
  const data = new Uint8Array(arrayBuffer);
  const workbook = XLSX.read(data, { type: "array" });
  const sheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[sheetName];
  const json = XLSX.utils.sheet_to_json(worksheet);

  return json.map((rawItem, index) => {
    const item = normalizeRow(rawItem);

    const sku = item["SKU"] ?? item["sku"] ?? item["کد"];
    const basePrice = item["قیمت پایه"] ?? item["قیمت"];
    const buyPrice = item["قیمت خرید"] ?? item["buyPrice"];
    const weight = item["وزن"] ?? item["weight"];

    if (!sku || basePrice === undefined || basePrice === null) {
      throw new Error(
        `ردیف ${index + 2}: ستون SKU یا قیمت پایه موجود نیست`
      );
    }

    const parsedBuyPrice =
      buyPrice !== undefined && buyPrice !== null && buyPrice !== "-" && buyPrice !== ""
        ? Number(buyPrice)
        : undefined;

    const parsedWeight =
      weight !== undefined && weight !== null && weight !== "-" && weight !== ""
        ? Number(weight)
        : undefined;

    return {
      sku: String(sku).trim(),
      basePrice: Number(basePrice),
      buyPrice: parsedBuyPrice,
      weight: parsedWeight,
      name: item["نام محصول"] || item["نام"] || "",
    };
  });
}

function PriceImportModal({ onClose, onSuccess }) {
  const [parsedData, setParsedData] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState("");

  const processFile = (file) => {
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "xlsx" && ext !== "xls") {
      setError("فقط فایل‌های Excel (.xlsx یا .xls) پشتیبانی می‌شوند");
      return;
    }

    setError("");
    setParsedData([]);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const priceData = parseWorkbook(e.target.result);
        setParsedData(priceData);
      } catch (err) {
        setError(err.message);
        setParsedData([]);
        setFileName("");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileInput = (event) => {
    processFile(event.target.files[0]);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleImport = async () => {
    if (!parsedData.length) return;

    setLoading(true);
    try {
      const items = parsedData.map(({ sku, basePrice, buyPrice, weight }) => {
        const item = { sku, basePrice };
        if (buyPrice !== undefined) item.buyPrice = buyPrice;
        if (weight !== undefined) item.weight = weight;
        return item;
      });
      const response = await putApi({
        path: "admin/products/updatePricesBySku",
        body: JSON.stringify({ items }),
      });

      const { updated, notFound } = response?.data?.result || {};

      if (notFound?.length) {
        errorMessage(
          `${notFound.length} محصول یافت نشد: SKU ${notFound.slice(0, 3).join(", ")}${notFound.length > 3 ? " ..." : ""}`
        );
      }

      if (updated > 0) {
        successMessage(`${updated} محصول با موفقیت به‌روزرسانی شد`);
        onSuccess?.();
        onClose?.();
      }
    } catch (err) {
      console.error(err);
      errorMessage("خطا در به‌روزرسانی قیمت‌ها");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col shadow-xl">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-gray-800 dark:text-white">
            ورود قیمت از اکسل
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          فایل اکسل باید شامل ستون‌های <strong>SKU</strong> و{" "}
          <strong>قیمت پایه</strong> باشد. ستون‌های <strong>قیمت خرید</strong> و <strong>وزن</strong> اختیاری هستند.
        </p>

        {/* Drop zone */}
        <label
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer mb-4 transition-colors
            ${isDragging
              ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
              : "border-gray-300 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-gray-600"
            }`}
        >
          <span className="text-sm text-gray-500 dark:text-gray-400 select-none">
            {fileName
              ? fileName
              : isDragging
              ? "فایل را رها کنید"
              : "فایل را اینجا بکشید یا کلیک کنید"}
          </span>
          <span className="text-xs text-gray-400 dark:text-gray-500 mt-1 select-none">
            xlsx ,xls
          </span>
          <input
            type="file"
            accept=".xlsx, .xls"
            onChange={handleFileInput}
            className="hidden"
          />
        </label>

        {error && (
          <p className="text-red-500 text-sm mb-4 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
            {error}
          </p>
        )}

        {parsedData.length > 0 && (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
              {parsedData.length} محصول خوانده شد:
            </p>
            <div className="overflow-auto flex-1 border border-gray-200 dark:border-gray-700 rounded-lg mb-4">
              <table className="w-full text-sm text-right">
                <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                  <tr>
                    <th className="px-4 py-2 font-medium">نام محصول</th>
                    <th className="px-4 py-2 font-medium">SKU</th>
                    <th className="px-4 py-2 font-medium">قیمت پایه (تومان)</th>
                    <th className="px-4 py-2 font-medium">قیمت خرید (تومان)</th>
                    <th className="px-4 py-2 font-medium">وزن (گرم)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {parsedData.map((item, i) => (
                    <tr
                      key={i}
                      className="text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    >
                      <td className="px-4 py-2">{item.name || "-"}</td>
                      <td className="px-4 py-2">{item.sku}</td>
                      <td className="px-4 py-2 font-mono">
                        {digitsThree(item.basePrice)}
                      </td>
                      <td className="px-4 py-2 font-mono">
                        {item.buyPrice !== undefined ? digitsThree(item.buyPrice) : "-"}
                      </td>
                      <td className="px-4 py-2 font-mono">
                        {item.weight !== undefined ? item.weight : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex gap-3 justify-start">
              <button
                onClick={handleImport}
                disabled={loading}
                type="button"
                className="px-5 py-2.5 text-sm rounded-lg text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "در حال به‌روزرسانی..." : "تایید و اعمال"}
              </button>
              <button
                onClick={onClose}
                type="button"
                className="px-5 py-2.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                انصراف
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default PriceImportModal;